/**
 * The pure update-decision function (web-client.md §7). Given the running client's version + protocol
 * against the server's version manifest, decide whether to do nothing, softly notify ("new version –
 * reload"), or force a blocking reload. This is the single source of truth the SW-update glue and the
 * diagnostics page both consult; the DOM/SW runtime is intentionally kept out so this is unit-tested
 * as plain logic. DOM-free, pure.
 */
import type { ClientVersionInfo, VersionManifest } from '../rest/versionClient.ts';
import { compareSemver } from './semver.ts';

/** `none` = up to date/notify not needed; `notify` = soft toast; `force` = block + hard reload. */
export type UpdateAction = 'none' | 'notify' | 'force';

export interface UpdateDecision {
  action: UpdateAction;
  /** Machine-readable cause, for logs/telemetry (`up-to-date` | `below-latest` | `below-min` | ...). */
  reason: string;
  /** The human upgrade instruction from the manifest, when an update is indicated. */
  instructions: string | null;
}

export interface UpdateInput {
  clientVersion: string;
  clientProtocol: number;
  manifest: VersionManifest;
  /** Which manifest entry applies to us (default `web`). */
  clientType?: string;
}

const UP_TO_DATE: UpdateDecision = { action: 'none', reason: 'up-to-date', instructions: null };

/**
 * Decide the update action from a fetched manifest. Precedence (strongest first):
 *   1. protocol below the server's minimum        -> force
 *   2. client version below minSupportedVersion    -> force
 *   3. client version below latestVersion          -> notify
 *   4. otherwise                                    -> none
 */
export function decideUpdate(input: UpdateInput): UpdateDecision {
  const { manifest, clientVersion, clientProtocol } = input;
  const clientType = input.clientType ?? 'web';
  const info: ClientVersionInfo | undefined = manifest.clients[clientType];
  const instructions = info?.upgrade.instructions ?? null;

  if (clientProtocol < manifest.minProtocolVersion) {
    return { action: 'force', reason: 'protocol-unsupported', instructions };
  }
  if (!info) {
    // No window for our client type: tolerant reader, treat as up to date (protocol.md §11).
    return UP_TO_DATE;
  }
  if (compareSemver(clientVersion, info.minSupportedVersion) < 0) {
    return { action: 'force', reason: 'below-min', instructions };
  }
  if (compareSemver(clientVersion, info.latestVersion) < 0) {
    return { action: 'notify', reason: 'below-latest', instructions };
  }
  return UP_TO_DATE;
}

/**
 * The server told us at the handshake to upgrade (sys.error UPGRADE_REQUIRED, protocol.md §10). That
 * is always a hard block — no gameplay below minSupportedVersion is reachable (web-client.md §7).
 */
export function decideForcedReload(): UpdateDecision {
  return { action: 'force', reason: 'server-upgrade-required', instructions: null };
}
